import i18next from 'i18next';
import {initReactI18next, useTranslation} from 'react-i18next';
import * as RNLocalize from 'react-native-localize';
import {I18nManager} from 'react-native';
import memoize from 'lodash.memoize';

import ExistLanguages, {getLanguageResources} from './loaders';

// Type that represents supported language codes
type SupportedLanguage = keyof typeof ExistLanguages;

/**
 * The tr function is a memoized translation function that can be used in non-hook contexts
 * such as utility functions, class components, or any place where hooks can't be used.
 *
 * Example usage:
 * import { tr } from './translations';
 *
 * function showError() {
 *   alert(tr('app.error'));
 * }
 */
export const tr = memoize(
  (key: string, options = {}): string => i18next.t(key, options) as string,
  (key: string, options = {}) =>
    options ? key + JSON.stringify(options) : key,
);

// Initialize i18next with default configuration
i18next.use(initReactI18next).init({
  resources: getLanguageResources(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

// Export the initialized i18next instance
export const i18n = i18next;

// Function to change the language
export default (language?: string) => {
  // Clear memoization cache if available
  if (tr.cache && tr.cache.clear) {
    tr.cache.clear();
  }

  // If no language specified, find the best match from device
  if (!language) {
    const supportedCodes = Object.keys(ExistLanguages);
    const bestLanguage = RNLocalize.getLocales().find(locale =>
      supportedCodes.includes(locale.languageCode),
    );
    language = bestLanguage?.languageCode || 'en';
  }

  // Check if language exists in our supported languages and typecast to ensure safe access
  const safeLanguage = (
    ExistLanguages.hasOwnProperty(language) ? language : 'en'
  ) as SupportedLanguage;

  // Set RTL based on language
  const isRTL = ExistLanguages[safeLanguage].isRTL;
  I18nManager.forceRTL(isRTL);

  // Change i18next language
  i18next.changeLanguage(safeLanguage);
};

// Export useTranslation hook for direct use in components
export {useTranslation};

// Set initial language based on device
const supportedCodes = Object.keys(ExistLanguages);
const bestLanguage = RNLocalize.getLocales().find(locale =>
  supportedCodes.includes(locale.languageCode),
);
const initialLanguage = (bestLanguage?.languageCode ||
  'en') as SupportedLanguage;
i18next.changeLanguage(initialLanguage);
